import type { NavItemConfig } from '@/types/nav'
import type { User } from '@/types/user'
import { paths } from '@/paths'
import { Role } from '@/lib/auth/role'
import { TYPEFORM_BUG_REPORT_ID, TYPEFORM_FEATURE_REQUEST_ID } from '@/lib/typeform'

// NOTE: First level elements are groups.

// Other properties:
// label: 'New'
// external: true (for external link), href: [link]

export interface LayoutConfig {
	navItems: NavItemConfig[]
}

export const getLayoutConfig = (user: User | null): LayoutConfig =>
	({
		navItems: [
			{
				key: '',
				title: '',
				items: [
					{
						key: 'overview',
						title: 'Overview',
						href: paths.dashboard.overview,
						icon: 'house',
					},
					{
						key: 'health',
						title: 'Health',
						href: paths.dashboard.health,
						requiresHealthAccess: true,
						icon: 'heartbeat',
						label: 'Beta',
					},
					// {
					// 	key: 'copilot',
					// 	title: 'AI Assistant',
					// 	// label: 'Beta',
					// 	requiresBetaAccess: true,
					// 	href: paths.dashboard.copilot.list,
					// 	icon: 'magic-wand',
					// },
					{
						key: 'analytics',
						title: 'Analytics',
						icon: 'chart-pie',
						href:
							user?.role === Role.Admin ? paths.dashboard.analytics.collections : paths.dashboard.analytics.inventory,
						matcher: { type: 'startsWith', href: '/dashboard/analytics' },
					},
					{
						key: 'market-insights',
						title: 'Market Insights',
						icon: 'chart-line-up',
						label: 'Beta',
						requiresMarketInsightsAccess: true,
						href: paths.dashboard.marketInsights.overview,
						matcher: { type: 'startsWith', href: '/dashboard/market-insights' },
					},
					{
						key: 'location-insights',
						title: 'Location Insights',
						icon: 'footprints',
						label: 'Beta',
						requiresFootTrafficAccess: true,
						href: paths.dashboard.locationInsights.overview,
						matcher: { type: 'startsWith', href: '/dashboard/location-insights' },
					},
					{
						key: 'stores',
						title: 'Stores',
						icon: 'store',
						href: paths.dashboard.stores.list,
						matcher: { type: 'startsWith', href: '/dashboard/stores' },
					},
					{
						key: 'products',
						title: 'Products',
						icon: 'shopping-bag-open',
						href: paths.dashboard.products.list,
						matcher: { type: 'startsWith', href: '/dashboard/products' },
					},
					{
						key: 'inventory',
						title: 'Inventory',
						icon: 'package',
						label: 'New',
						seenFeatureKey: 'inventory',
						requiresInventoryAccess: true,
						href: paths.dashboard.inventory.list,
						matcher: { type: 'startsWith', href: '/dashboard/inventory' },
					},
					{
						key: 'restocks',
						title: 'Restocks',
						icon: 'truck',
						href: paths.dashboard.restocks.list,
						matcher: { type: 'startsWith', href: '/dashboard/restocks' },
					},
					{
						key: 'transactions',
						title: 'Transactions',
						icon: 'receipt',
						href: paths.dashboard.transactions.list,
						matcher: { type: 'startsWith', href: '/dashboard/transactions' },
					},
					{
						key: 'marketing',
						title: 'Marketing',
						icon: 'video',
						href: paths.dashboard.marketing.list,
						matcher: { type: 'startsWith', href: '/dashboard/marketing' },
					},
					{
						key: 'promotions',
						title: 'Promotions',
						icon: 'tag',
						label: 'New',
						seenFeatureKey: 'promotions',
						href: paths.dashboard.promotions.list,
						matcher: { type: 'startsWith', href: '/dashboard/promotions' },
					},
					{
						key: 'finances',
						title: 'Finances',
						icon: 'chart-line',
						href: paths.dashboard.finances.list,
					},
					{
						key: 'help',
						title: 'Help',
						icon: 'question',
						expandNavWhenMini: true,
						items: [
							{
								key: 'help-center',
								title: 'Help center',
								href: paths.learn,
								external: true,
							},
							{
								key: 'submit-bug',
								title: 'Report a bug',
								typeformId: TYPEFORM_BUG_REPORT_ID,
							},
							{
								key: 'submit-feature',
								title: 'Request a feature',
								typeformId: TYPEFORM_FEATURE_REQUEST_ID,
							},
						],
					},
					{
						key: 'settings',
						title: 'Settings',
						icon: 'gear',
						href: paths.dashboard.settings.account,
						matcher: { type: 'startsWith', href: '/dashboard/settings' },
					},
				],
			},
		],
	}) satisfies LayoutConfig
